import { useEffect, useState } from "react"

import { Button, Flex, Form, Modal, Statistic, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import InputComponent from "../../../../Components/Input"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import utils from "../../../../Utils/utils"
import UserCreators from "../../../User/reducer"

import "./ModalChangeEmail.less"

const { Title, Text } = Typography
const { Countdown } = Statistic
const { changeEmailOnboardingRequest, userErrorsReset } = UserCreators

const ModalChangEmail = ({ open, initStep, onClose, currentEmail, resendDeadline, onResend }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const emailChanged = useSelector((state) => state.user.emailChanged)
  const saveFetching = useSelector((state) => state.user.saveFetching)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const errors = useSelector((state) => state.user.errors)
  const [step, setStep] = useState(initStep)
  const [email, setEmail] = useState(currentEmail)
  const [fieldError, setFieldError] = useState([])
  const isForm = step === "form"
  const backendEmailError =
    typeof errors === "string"
      ? errors
      : errors?.new_email?.[0] ||
        errors?.new_email ||
        errors?.detail ||
        errors?.message?.detail ||
        errors?.message ||
        fieldError[0] ||
        null

  const emailError = backendEmailError || fieldError[0] || null

  const handleContinue = async () => {
    try {
      setFieldError([])
      const values = await form.validateFields(["email"])
      onSubmit(values)
    } catch (errorInfo) {
      const emailField = errorInfo?.errorFields?.find(({ name }) => name?.[0] === "email")
      setFieldError(emailField?.errors || [])
    }
  }

  const onSubmit = ({ email }) => {
    dispatch(changeEmailOnboardingRequest({ new_email: email }))
  }
  const handleCancel = () => {
    setStep("form")
    setFieldError([])
    form.resetFields()
    dispatch(userErrorsReset())
    onClose()
  }
  const onFinish = () => {
    return null
  }

  const handleResend = () => {
    onResend()
  }

  useEffect(() => {
    if (open) {
      setStep(initStep)
    }
  }, [initStep, open])

  useEffect(() => {
    if (open) {
      setEmail(currentEmail)
      setFieldError([])
      form.setFieldsValue({ email: currentEmail })
    }
  }, [currentEmail, open, form])

  useEffect(() => {
    if (!emailChanged || !open) return

    handleResend()
    setStep("sent")
  }, [emailChanged])

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable
      width={442}
      centered={!isMobile}
      styles={{
        content: { borderRadius: !isMobile && 20, padding: 16 }
      }}
      closeIcon={<Icon type="cross_small" />}
      classNames={{
        content: "clear-bg"
      }}
    >
      <div style={{ display: "flex", gap: 20 }}>
        <div style={{ flex: 1 }}>
          {isForm ? (
            <>
              <Title level={3} style={{ textAlign: "left" }}>
                Change email
              </Title>

              <Text type="secondary">
                Provide a new email address to which you would like to change your current one.
              </Text>

              <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                onValuesChange={(_, values) => {
                  setEmail(values.email)
                  if (fieldError.length) {
                    setFieldError([])
                  }
                  if (backendEmailError) {
                    dispatch(userErrorsReset())
                  }
                }}
                initialValues={{ email: currentEmail }}
                style={{ marginTop: 16 }}
              >
                <p style={{ marginBottom: 8 }}>
                  <Text>
                    New email address <span style={{ color: "#225AEA" }}>*</span>
                  </Text>
                </p>
                <Form.Item
                  name="email"
                  validateStatus={emailError ? "error" : null}
                  rules={[
                    {
                      type: "email",
                      message: t("The email you entered is incorrect.")
                    },
                    {
                      required: true,
                      message: t("This field is required")
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputComponent
                    placeholder="newemail@gmail.com"
                    size="large"
                    name="email"
                    type="email"
                    autoComplete="email"
                  />
                </Form.Item>
                {emailError ? (
                  <div style={{ marginTop: 8 }}>
                    <Text type="danger">{emailError}</Text>
                  </div>
                ) : null}
              </Form>

              <div className="modal-change-email-actions">
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="primary" loading={saveFetching} onClick={handleContinue}>
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <Icon type="email_large" />
              </div>
              <Title level={3} style={{ textAlign: "left" }}>
                You&apos;ve got mail
              </Title>

              <p>
                <Text type="secondary">
                  Check the email letter sent to{" "}
                  <Text>
                    <Private>{email}</Private>
                  </Text>
                  . The new email address won&apos;t take effect until it is confirmed.
                </Text>
              </p>

              <Flex
                align="center"
                justify="space-between"
                style={{
                  marginTop: 20,
                  flexDirection: isMobile && "column-reverse"
                }}
              >
                <div className="resend-timer">
                  {resendDeadline && resendDeadline > Date.now() ? (
                    <Countdown
                      title=""
                      value={resendDeadline}
                      onFinish={onFinish}
                      format="mm:ss"
                      prefix={
                        <Text type="secondary" strong>
                          {t("You will be able to resend code in")}{" "}
                        </Text>
                      }
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>
                      {t("Didn’t receive an email?")}{" "}
                      <span className="text-link" onClick={handleResend}>
                        {t("Resend now")}
                      </span>
                    </span>
                  )}
                </div>

                <ButtonComponent
                  size="large"
                  href={`https://mail.${utils.checkMailDomain(email).link}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    marginRight: "auto",
                    width: isMobile && "100%",
                    justifyContent: isMobile && "center",
                    marginBottom: isMobile && 14
                  }}
                  className="flex"
                >
                  <img
                    className="btn-icon icon-space"
                    src={Images[utils.checkMailDomain(email).type]}
                    alt=""
                  />
                  {t("Go To")} {utils.checkMailDomain(email).name}
                </ButtonComponent>
              </Flex>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ModalChangEmail
